import { useEffect, useMemo, useState } from 'react'
import {
  FaArrowLeft,
  FaBullhorn,
  FaChartLine,
  FaDollarSign,
  FaEye,
  FaMapMarkerAlt,
  FaRoute,
  FaSatelliteDish,
  FaShieldAlt,
  FaSignOutAlt,
  FaTrash,
  FaUpload,
  FaUser,
  FaUsers
} from 'react-icons/fa'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import api from 'services/api'

import * as S from '../style'
import * as A from './style'

type AdFormat = 'horizontal' | 'card'
type AdStatus = 'active' | 'inactive'

type AdminAd = {
  id: string
  campaignName: string
  partnerName: string
  destinationUrl: string
  imageUrl: string
  mobileImageUrl?: string | null
  format: AdFormat
  status: AdStatus
  priority: number
  contractValue?: number | null
  commercialUserId?: string | null
  commercialName?: string
  commercialEmail?: string
  commissionRate?: number | null
  commissionAmount?: number | null
  commissionStatus?: 'pending' | 'paid'
  startDate?: string | null
  endDate?: string | null
  localityCount: number
  localityNames: string[]
  impressions: number
  clicks: number
  ctr: number
}

type AdsResponse = {
  items: AdminAd[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  summary: {
    total: number
    active: number
    inactive: number
    horizontal: number
    cards: number
  }
}

type LocalityOption = {
  destinationKey: string
  destinationKeys?: string[]
  sourceName: string
  displayName: string
}

type LocalitiesResponse = {
  items: LocalityOption[]
}

type CommercialUser = {
  id: string
  firstname: string
  lastname: string
  email: string
  country?: string
}

type CommercialUsersResponse = {
  items: CommercialUser[]
}

type AdDetails = Omit<
  AdminAd,
  'localityCount' | 'localityNames' | 'impressions' | 'clicks' | 'ctr'
> & {
  localities: LocalityOption[]
}

const initialForm = {
  campaignName: '',
  partnerName: '',
  destinationUrl: '',
  imageUrl: '',
  mobileImageUrl: '',
  format: 'card' as AdFormat,
  status: 'active' as AdStatus,
  contractValue: '',
  commercialUserId: '',
  commissionRate: '',
  commissionStatus: 'pending' as 'pending' | 'paid',
  startDate: '',
  endDate: ''
}

const asDateInput = (value?: string | null) =>
  value ? String(value).slice(0, 10) : ''

const AdminAds = () => {
  const { user } = useUser()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [format, setFormat] = useState('all')
  const [page, setPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [adToDelete, setAdToDelete] = useState<AdminAd | null>(null)
  const [deletingAd, setDeletingAd] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [loadingForm, setLoadingForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingMobileImage, setUploadingMobileImage] = useState(false)
  const [form, setForm] = useState(initialForm)
  const [selectedLocalities, setSelectedLocalities] = useState<
    LocalityOption[]
  >([])
  const [localitySearch, setLocalitySearch] = useState('')
  const [debouncedLocalitySearch, setDebouncedLocalitySearch] = useState('')

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ')
  const isCommercialOnly = Boolean(user?.isCommercial && !user?.isAdmin)
  const roleLabel = user?.isMaster
    ? 'Master'
    : user?.isAdmin
      ? 'Admin'
      : 'Comercial'

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 350)

    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedLocalitySearch(localitySearch.trim())
    }, 300)

    return () => window.clearTimeout(timer)
  }, [localitySearch])

  useEffect(() => {
    setPage(1)
  }, [status, format])

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: '20',
      status,
      format
    })

    if (debouncedSearch) params.set('search', debouncedSearch)
    return params.toString()
  }, [debouncedSearch, format, page, status])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-ads', queryString],
    queryFn: async () => {
      const response = await api.get<AdsResponse>(`/admin/ads?${queryString}`)
      return response.data
    },
    staleTime: 20 * 1000
  })

  const { data: commercialUsers } = useQuery({
    queryKey: ['admin-commercial-users'],
    queryFn: async () => {
      const response = await api.get<CommercialUsersResponse>(
        '/admin/ads/commercial-users'
      )
      return response.data
    },
    enabled: modalOpen,
    staleTime: 60 * 1000
  })

  const {
    data: localityOptions,
    isFetching: loadingLocalities,
    isError: localitySearchError
  } = useQuery({
    queryKey: ['admin-ad-locality-options', debouncedLocalitySearch],
    queryFn: async () => {
      const params = new URLSearchParams({
        search: debouncedLocalitySearch,
        limit: '20'
      })
      const response = await api.get<LocalitiesResponse>(
        `/admin/ads/localities/search?${params.toString()}`
      )
      return response.data
    },
    enabled: modalOpen && debouncedLocalitySearch.length >= 2,
    staleTime: 60 * 1000,
    retry: 1
  })

  const resetForm = () => {
    setEditingId(null)
    setForm(initialForm)
    setSelectedLocalities([])
    setLocalitySearch('')
    setDebouncedLocalitySearch('')
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    resetForm()
  }

  const openCreate = () => {
    resetForm()
    setModalOpen(true)
  }

  const openEdit = async (id: string) => {
    setModalOpen(true)
    setEditingId(id)
    setLoadingForm(true)
    setLocalitySearch('')

    try {
      const response = await api.get<AdDetails>(`/admin/ads/${id}`)
      const ad = response.data

      setForm({
        campaignName: ad.campaignName || '',
        partnerName: ad.partnerName || '',
        destinationUrl: ad.destinationUrl || '',
        imageUrl: ad.imageUrl || '',
        mobileImageUrl: ad.mobileImageUrl || '',
        format: ad.format || 'card',
        status: ad.status || 'active',
        contractValue:
          ad.contractValue === null || ad.contractValue === undefined
            ? ''
            : String(ad.contractValue),
        commercialUserId: ad.commercialUserId || '',
        commissionRate:
          ad.commissionRate === null || ad.commissionRate === undefined
            ? ''
            : String(ad.commissionRate),
        commissionStatus: ad.commissionStatus || 'pending',
        startDate: asDateInput(ad.startDate),
        endDate: asDateInput(ad.endDate)
      })
      setSelectedLocalities(ad.localities || [])
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível abrir o anúncio.',
        { theme: 'dark' }
      )
      setModalOpen(false)
      resetForm()
    } finally {
      setLoadingForm(false)
    }
  }

  const toggleLocality = (locality: LocalityOption) => {
    setSelectedLocalities((current) => {
      const exists = current.some(
        (item) => item.destinationKey === locality.destinationKey
      )

      if (exists) {
        return current.filter(
          (item) => item.destinationKey !== locality.destinationKey
        )
      }

      return [...current, locality]
    })
  }

  const uploadAdImage = async (
    file: File | undefined,
    field: 'imageUrl' | 'mobileImageUrl'
  ) => {
    if (!file) return

    const setUploading =
      field === 'imageUrl' ? setUploadingImage : setUploadingMobileImage

    setUploading(true)

    try {
      const data = new FormData()
      data.append('file', file)
      const response = await api.post('/users/uploadFile', data)
      const url = response.data?.locationInS3

      if (!url) {
        throw new Error('Upload sem URL de retorno')
      }

      setField(field, url)
      toast.success('Imagem enviada.', { theme: 'dark' })
    } catch {
      toast.error('Não foi possível enviar a imagem.', { theme: 'dark' })
    } finally {
      setUploading(false)
    }
  }

  const saveAd = async () => {
    if (saving) return

    if (!selectedLocalities.length) {
      toast.warning('Selecione pelo menos uma localidade.', { theme: 'dark' })
      return
    }

    setSaving(true)

    const payload = {
      ...form,
      contractValue: form.contractValue
        ? Number(String(form.contractValue).replace(',', '.'))
        : null,
      commercialUserId: form.commercialUserId || null,
      commissionRate: form.commissionRate
        ? Number(String(form.commissionRate).replace(',', '.'))
        : null,
      commissionStatus: form.commissionStatus,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      mobileImageUrl: form.mobileImageUrl || null,
      localityKeys: Array.from(
        new Set(
          selectedLocalities.flatMap((item) =>
            item.destinationKeys?.length
              ? item.destinationKeys
              : [item.destinationKey]
          )
        )
      )
    }

    try {
      if (editingId) {
        await api.patch(`/admin/ads/${editingId}`, payload)
        toast.success('Anúncio atualizado.', { theme: 'dark' })
      } else {
        await api.post('/admin/ads', payload)
        toast.success('Anúncio criado.', { theme: 'dark' })
      }

      await queryClient.invalidateQueries({ queryKey: ['admin-ads'] })
      closeModal()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível salvar o anúncio.',
        { theme: 'dark' }
      )
    } finally {
      setSaving(false)
    }
  }

  const removeAd = (ad: AdminAd) => {
    if (deletingAd) return
    setAdToDelete(ad)
  }

  const confirmRemoveAd = async () => {
    if (!adToDelete || deletingAd) return

    setDeletingAd(true)

    try {
      await api.delete(`/admin/ads/${adToDelete.id}`)
      await queryClient.invalidateQueries({ queryKey: ['admin-ads'] })
      toast.success('Anúncio excluído.', { theme: 'dark' })
      setAdToDelete(null)
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível excluir o anúncio.',
        { theme: 'dark' }
      )
    } finally {
      setDeletingAd(false)
    }
  }

  const setField = (field: keyof typeof initialForm, value: any) =>
    setForm((current) => ({ ...current, [field]: value }))

  const normalizeDestinationUrlInput = () => {
    const value = form.destinationUrl.trim()
    if (!value) return

    if (!/^https?:\/\//i.test(value)) {
      setField('destinationUrl', `https://${value}`)
    }
  }

  return (
    <S.Page>
      <S.Sidebar>
        <S.BrandBlock>
          <S.BrandLink to="/">
            <S.Logo src="/images/header-logo.png" alt="RedSterna" />
          </S.BrandLink>
          <S.AdminBadge>
            <FaShieldAlt />
            {roleLabel}
          </S.AdminBadge>
        </S.BrandBlock>

        <S.SidebarBody>
          <S.SidebarLabel>Gestão</S.SidebarLabel>
          <S.SidebarNav>
            {!isCommercialOnly && (
              <>
                <S.SidebarNavLink to="/admin">
                  <FaChartLine />
                  Dashboard
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/usuarios">
                  <FaUsers />
                  Usuários
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/localidades">
                  <FaMapMarkerAlt />
                  Localidades
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/roteiros">
                  <FaRoute />
                  Roteiros
                </S.SidebarNavLink>
              </>
            )}
            <S.SidebarNavLink to="/admin/anuncios" $active>
              <FaBullhorn />
              Anúncios
            </S.SidebarNavLink>
            {!isCommercialOnly && (
              <S.SidebarNavLink to="/admin/radar">
                <FaSatelliteDish />
                Radar
              </S.SidebarNavLink>
            )}
            <S.SidebarNavLink to="/admin/comissoes">
              <FaDollarSign />
              Comissões
            </S.SidebarNavLink>
          </S.SidebarNav>
        </S.SidebarBody>

        <S.SidebarFooter>
          <S.SidebarLink to="/minha-conta">
            <FaUser />
            Meu perfil
          </S.SidebarLink>
          <S.SidebarLink to="/">
            <FaArrowLeft />
            Voltar ao site
          </S.SidebarLink>
        </S.SidebarFooter>
      </S.Sidebar>

      <S.Main>
        <S.Topbar>
          <div>
            <S.TopbarEyebrow>RedSterna Admin</S.TopbarEyebrow>
            <S.TopbarTitle>Anúncios</S.TopbarTitle>
          </div>

          <S.UserArea>
            <S.UserAvatar>
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={fullName || 'Administrador'}
                />
              ) : (
                <FaUser />
              )}
            </S.UserAvatar>
            <S.UserInfo>
              <strong>{fullName || 'Administrador'}</strong>
              <span>{user?.email}</span>
            </S.UserInfo>
            <S.AdminChip>{roleLabel}</S.AdminChip>
            <S.SignOutButton type="button" onClick={signOut} aria-label="Sair">
              <FaSignOutAlt />
            </S.SignOutButton>
          </S.UserArea>
        </S.Topbar>

        <S.Content>
          <A.PageHeader>
            <div>
              <h1>
                {isCommercialOnly ? 'Meus anúncios' : 'Gestão de anúncios'}
              </h1>
              <p>
                {isCommercialOnly
                  ? 'Cadastre as parcerias da sua carteira. Novos anúncios ficam inativos até a aprovação.'
                  : 'Gerencie parceiros locais e defina em quais fichas de destino cada anúncio será exibido.'}
              </p>
            </div>
            <A.PrimaryButton type="button" onClick={openCreate}>
              <FaBullhorn />
              Novo anúncio
            </A.PrimaryButton>
          </A.PageHeader>

          {data && (
            <S.UserSummaryGrid>
              <S.UserSummaryCard>
                <FaBullhorn />
                <div>
                  <strong>{data.summary.total}</strong>
                  <span>Anúncios</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="green">
                <FaEye />
                <div>
                  <strong>{data.summary.active}</strong>
                  <span>Ativos</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="blue">
                <FaRoute />
                <div>
                  <strong>{data.summary.horizontal}</strong>
                  <span>Horizontais</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="red">
                <FaMapMarkerAlt />
                <div>
                  <strong>{data.summary.cards}</strong>
                  <span>Retangulares</span>
                </div>
              </S.UserSummaryCard>
            </S.UserSummaryGrid>
          )}

          <S.UserToolbar>
            <S.UserSearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por campanha ou parceiro..."
            />
            <S.UserFilter
              value={format}
              onChange={(event) => setFormat(event.target.value)}
            >
              <option value="all">Todos os formatos</option>
              <option value="horizontal">Horizontal</option>
              <option value="card">Retangular</option>
            </S.UserFilter>
            <S.UserFilter
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </S.UserFilter>
          </S.UserToolbar>

          {isLoading && <S.StateCard>Carregando anúncios...</S.StateCard>}

          {isError && (
            <S.StateCard $error>
              Não foi possível carregar os anúncios.
            </S.StateCard>
          )}

          {data && (
            <A.TableCard>
              <A.TableScroll>
                <A.AdTable>
                  <thead>
                    <tr>
                      <th>Anúncio</th>
                      {!isCommercialOnly && <th>Comercial</th>}
                      <th>Formato</th>
                      <th>Localidades</th>
                      <th>Comissão</th>
                      <th>Status</th>
                      <th>Views</th>
                      <th>Cliques</th>
                      <th>CTR</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((ad) => (
                      <tr key={ad.id}>
                        <td>
                          <A.AdIdentity>
                            <img src={ad.imageUrl} alt="" />
                            <div>
                              <strong>{ad.campaignName}</strong>
                              <span>{ad.partnerName}</span>
                            </div>
                          </A.AdIdentity>
                        </td>
                        {!isCommercialOnly && (
                          <td>
                            <A.LocalityCell>
                              <strong>{ad.commercialName || '—'}</strong>
                              <span>
                                {ad.commercialEmail || 'Sem responsável'}
                              </span>
                            </A.LocalityCell>
                          </td>
                        )}
                        <td>
                          <A.FormatBadge $format={ad.format}>
                            {ad.format === 'horizontal'
                              ? 'Horizontal'
                              : 'Retangular'}
                          </A.FormatBadge>
                        </td>
                        <td>
                          <A.LocalityCell>
                            <strong>{ad.localityCount}</strong>
                            <span>
                              {ad.localityNames.slice(0, 2).join(', ') ||
                                'Sem localidade'}
                              {ad.localityNames.length > 2 ? '…' : ''}
                            </span>
                          </A.LocalityCell>
                        </td>
                        <td>
                          <A.LocalityCell>
                            <strong>
                              {ad.commissionAmount == null
                                ? 'A definir'
                                : new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL'
                                  }).format(ad.commissionAmount)}
                            </strong>
                            <span>
                              {ad.commissionRate == null
                                ? 'Sem percentual'
                                : `${ad.commissionRate}% · ${
                                    ad.commissionStatus === 'paid'
                                      ? 'Pago'
                                      : 'Pendente'
                                  }`}
                            </span>
                          </A.LocalityCell>
                        </td>
                        <td>
                          <A.StatusBadge $active={ad.status === 'active'}>
                            {ad.status === 'active' ? 'Ativo' : 'Inativo'}
                          </A.StatusBadge>
                        </td>
                        <td>{ad.impressions}</td>
                        <td>{ad.clicks}</td>
                        <td>{ad.ctr.toFixed(1)}%</td>
                        <td>
                          <A.Actions>
                            <button
                              type="button"
                              onClick={() => openEdit(ad.id)}
                            >
                              Editar
                            </button>
                            {!isCommercialOnly && (
                              <button
                                type="button"
                                className="danger"
                                onClick={() => removeAd(ad)}
                              >
                                Excluir
                              </button>
                            )}
                          </A.Actions>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </A.AdTable>
              </A.TableScroll>

              {data.items.length === 0 && (
                <A.EmptyState>
                  Nenhum anúncio encontrado com os filtros selecionados.
                </A.EmptyState>
              )}

              <S.Pagination>
                <span>
                  {data.pagination.total} anúncios · página{' '}
                  {data.pagination.page} de {data.pagination.pages}
                </span>
                <div>
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={page >= data.pagination.pages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </S.Pagination>
            </A.TableCard>
          )}
        </S.Content>
      </S.Main>

      {adToDelete && (
        <S.AdminModalOverlay
          role="presentation"
          onMouseDown={() => !deletingAd && setAdToDelete(null)}
        >
          <A.ConfirmModal
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-ad-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <A.ConfirmIcon>
              <FaTrash />
            </A.ConfirmIcon>
            <A.ConfirmContent>
              <h2 id="delete-ad-title">Excluir anúncio?</h2>
              <p>
                Você está prestes a excluir “{adToDelete.campaignName}”. Esta
                ação é permanente e o anúncio deixará de aparecer nas fichas de
                destino vinculadas.
              </p>
            </A.ConfirmContent>
            <S.AdminModalActions>
              <button
                type="button"
                onClick={() => setAdToDelete(null)}
                disabled={deletingAd}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="danger"
                onClick={confirmRemoveAd}
                disabled={deletingAd}
              >
                {deletingAd ? 'Excluindo...' : 'Excluir anúncio'}
              </button>
            </S.AdminModalActions>
          </A.ConfirmModal>
        </S.AdminModalOverlay>
      )}

      {modalOpen && (
        <S.AdminModalOverlay onMouseDown={closeModal}>
          <S.AdminModal
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <S.AdminModalHeader>
              <div>
                <S.TopbarEyebrow>
                  {editingId ? 'Editar campanha' : 'Nova campanha'}
                </S.TopbarEyebrow>
                <h2>{editingId ? 'Editar anúncio' : 'Criar anúncio'}</h2>
              </div>
              <button type="button" onClick={closeModal}>
                ×
              </button>
            </S.AdminModalHeader>

            {loadingForm ? (
              <S.StateCard>Carregando anúncio...</S.StateCard>
            ) : (
              <>
                <A.FormGrid>
                  <A.Field>
                    <label>Nome interno da campanha *</label>
                    <input
                      value={form.campaignName}
                      onChange={(event) =>
                        setField('campaignName', event.target.value)
                      }
                      placeholder="Ex: Setembro · Hotel Paris"
                    />
                  </A.Field>

                  <A.Field>
                    <label>Nome do parceiro *</label>
                    <input
                      value={form.partnerName}
                      onChange={(event) =>
                        setField('partnerName', event.target.value)
                      }
                      placeholder="Ex: Le Charme Paris"
                    />
                  </A.Field>

                  <A.Field $wide>
                    <label>URL de destino *</label>
                    <input
                      value={form.destinationUrl}
                      onChange={(event) =>
                        setField('destinationUrl', event.target.value)
                      }
                      onBlur={normalizeDestinationUrlInput}
                      placeholder="Ex: www.parceiro.com ou https://..."
                    />
                  </A.Field>

                  <A.Field $wide>
                    <label>Imagem principal *</label>
                    <A.ImageInputRow>
                      <input
                        value={form.imageUrl}
                        onChange={(event) =>
                          setField('imageUrl', event.target.value)
                        }
                        placeholder="Cole uma URL ou envie uma imagem"
                      />
                      <label>
                        <FaUpload />
                        {uploadingImage ? 'Enviando...' : 'Enviar imagem'}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          disabled={uploadingImage}
                          onChange={(event) =>
                            uploadAdImage(event.target.files?.[0], 'imageUrl')
                          }
                        />
                      </label>
                    </A.ImageInputRow>
                    <A.FieldHint>
                      {form.format === 'horizontal'
                        ? 'Recomendado: imagem horizontal em proporção 4:1, por exemplo 1600 × 400 px.'
                        : 'Recomendado: imagem em proporção 16:9, por exemplo 1200 × 675 px.'}
                    </A.FieldHint>
                  </A.Field>

                  <A.Field $wide>
                    <label>Imagem mobile</label>
                    <A.ImageInputRow>
                      <input
                        value={form.mobileImageUrl}
                        onChange={(event) =>
                          setField('mobileImageUrl', event.target.value)
                        }
                        placeholder="Opcional · usa a imagem principal quando vazio"
                      />
                      <label>
                        <FaUpload />
                        {uploadingMobileImage ? 'Enviando...' : 'Enviar imagem'}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          disabled={uploadingMobileImage}
                          onChange={(event) =>
                            uploadAdImage(
                              event.target.files?.[0],
                              'mobileImageUrl'
                            )
                          }
                        />
                      </label>
                    </A.ImageInputRow>
                    <A.FieldHint>
                      Opcional. Use quando quiser um corte específico para
                      celular.
                    </A.FieldHint>
                  </A.Field>

                  <A.Field $wide>
                    <label>Formato *</label>
                    <A.FormatSelector>
                      <A.FormatOption
                        type="button"
                        $active={form.format === 'horizontal'}
                        onClick={() => setField('format', 'horizontal')}
                      >
                        <span className="wide" />
                        <strong>Horizontal</strong>
                        <small>Régua premium em toda a largura da ficha</small>
                      </A.FormatOption>
                      <A.FormatOption
                        type="button"
                        $active={form.format === 'card'}
                        onClick={() => setField('format', 'card')}
                      >
                        <span className="card" />
                        <strong>Retangular</strong>
                        <small>Card padrão para parceiros locais</small>
                      </A.FormatOption>
                    </A.FormatSelector>
                  </A.Field>

                  <A.Field>
                    <label>Status</label>
                    <select
                      value={isCommercialOnly ? 'inactive' : form.status}
                      disabled={isCommercialOnly}
                      onChange={(event) =>
                        setField('status', event.target.value as AdStatus)
                      }
                    >
                      <option value="active">Ativo</option>
                      <option value="inactive">
                        {isCommercialOnly ? 'Aguardando aprovação' : 'Inativo'}
                      </option>
                    </select>
                    {isCommercialOnly && (
                      <A.FieldHint>
                        A ativação é feita por um Administrador ou pelo Master.
                      </A.FieldHint>
                    )}
                  </A.Field>

                  {!isCommercialOnly && (
                    <A.Field>
                      <label>Responsável comercial</label>
                      <select
                        value={form.commercialUserId}
                        onChange={(event) =>
                          setField('commercialUserId', event.target.value)
                        }
                      >
                        <option value="">Sem responsável comercial</option>
                        {commercialUsers?.items?.map((commercial) => (
                          <option key={commercial.id} value={commercial.id}>
                            {[commercial.firstname, commercial.lastname]
                              .filter(Boolean)
                              .join(' ') || commercial.email}
                          </option>
                        ))}
                      </select>
                    </A.Field>
                  )}

                  <A.Field>
                    <label>Valor do contrato (R$)</label>
                    <input
                      inputMode="decimal"
                      value={form.contractValue}
                      disabled={isCommercialOnly && Boolean(editingId)}
                      onChange={(event) =>
                        setField('contractValue', event.target.value)
                      }
                      placeholder="Opcional"
                    />
                    {isCommercialOnly && editingId && (
                      <A.FieldHint>
                        Após o cadastro, valores financeiros são ajustados pelo
                        Admin ou Master.
                      </A.FieldHint>
                    )}
                  </A.Field>

                  {!isCommercialOnly && (
                    <>
                      <A.Field>
                        <label>Comissão (%)</label>
                        <input
                          inputMode="decimal"
                          value={form.commissionRate}
                          onChange={(event) =>
                            setField('commissionRate', event.target.value)
                          }
                          placeholder="Ex: 15"
                        />
                      </A.Field>
                      <A.Field>
                        <label>Status da comissão</label>
                        <select
                          value={form.commissionStatus}
                          onChange={(event) =>
                            setField(
                              'commissionStatus',
                              event.target.value as 'pending' | 'paid'
                            )
                          }
                        >
                          <option value="pending">Pendente</option>
                          <option value="paid">Paga</option>
                        </select>
                      </A.Field>
                    </>
                  )}

                  <A.Field>
                    <label>Data inicial</label>
                    <input
                      type="date"
                      value={form.startDate}
                      onChange={(event) =>
                        setField('startDate', event.target.value)
                      }
                    />
                  </A.Field>

                  <A.Field>
                    <label>Data final</label>
                    <input
                      type="date"
                      value={form.endDate}
                      onChange={(event) =>
                        setField('endDate', event.target.value)
                      }
                    />
                  </A.Field>

                  <A.Field $wide>
                    <label>Localidades *</label>
                    <A.LocalityPicker>
                      <input
                        value={localitySearch}
                        onChange={(event) =>
                          setLocalitySearch(event.target.value)
                        }
                        placeholder="Busque uma cidade, região ou país..."
                      />

                      <A.SelectedLocations>
                        {selectedLocalities.map((locality) => (
                          <button
                            type="button"
                            key={locality.destinationKey}
                            onClick={() => toggleLocality(locality)}
                          >
                            {locality.displayName} ×
                          </button>
                        ))}
                        {!selectedLocalities.length && (
                          <span>Nenhuma localidade selecionada.</span>
                        )}
                      </A.SelectedLocations>

                      <A.LocalityResults>
                        {localitySearch.trim().length < 2 ? (
                          <span>Digite pelo menos 2 letras para buscar.</span>
                        ) : loadingLocalities ? (
                          <span>Buscando localidades...</span>
                        ) : localitySearchError ? (
                          <span>Não foi possível buscar as localidades.</span>
                        ) : localityOptions?.items?.length ? (
                          localityOptions.items.map((locality) => {
                            const selected = selectedLocalities.some(
                              (item) =>
                                item.destinationKey === locality.destinationKey
                            )
                            return (
                              <button
                                type="button"
                                key={locality.destinationKey}
                                className={selected ? 'selected' : ''}
                                onClick={() => toggleLocality(locality)}
                              >
                                <FaMapMarkerAlt />
                                <div>
                                  <strong>{locality.displayName}</strong>
                                  <span>{locality.sourceName}</span>
                                </div>
                                <b>{selected ? '✓' : '+'}</b>
                              </button>
                            )
                          })
                        ) : (
                          <span>Nenhuma localidade encontrada.</span>
                        )}
                      </A.LocalityResults>
                    </A.LocalityPicker>
                  </A.Field>

                  {form.imageUrl && (
                    <A.Field $wide>
                      <label>Prévia</label>
                      <A.Preview $format={form.format}>
                        <picture>
                          {form.mobileImageUrl && (
                            <source
                              media="(max-width: 620px)"
                              srcSet={form.mobileImageUrl}
                            />
                          )}
                          <img src={form.imageUrl} alt="Prévia do anúncio" />
                        </picture>
                      </A.Preview>
                    </A.Field>
                  )}
                </A.FormGrid>

                <S.AdminModalActions>
                  <button type="button" onClick={closeModal}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={saving}
                    onClick={saveAd}
                  >
                    {saving
                      ? 'Salvando...'
                      : editingId
                        ? 'Salvar alterações'
                        : 'Criar anúncio'}
                  </button>
                </S.AdminModalActions>
              </>
            )}
          </S.AdminModal>
        </S.AdminModalOverlay>
      )}
    </S.Page>
  )
}

export default AdminAds
